import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Menu,
  X,
  LogOut,
  Flag,
  ChevronDown,
  MessageSquareText,
  CalendarDays,
  Bookmark,
  Shield,
  Sparkles,
  CreditCard,
  Settings as SettingsIcon,
  Mic,
  Image as ImageIcon,
  Users,
  Signpost,
} from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";

const BetaPill = ({ color }) => (
  <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider ${color === "red" ? "bg-flag-red" : "bg-flag-navy"} text-white px-1.5 py-0.5 rounded`}>Beta</span>
);

// "Pro" badge — used on features that are gated behind the Pro tier (Voter
// List, Finances). Visually distinct from BetaPill so users can tell "this is
// a paid feature" from "this feature is still evolving."
const ProPill = () => (
  <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider bg-flag-red text-white px-1.5 py-0.5 rounded">Pro</span>
);

// "Communications" — anything the campaign publishes externally.
const communicationsChildren = [
  { to: "/generator", label: "Social Media Post Generator", desc: "Draft, refine, and copy posts.", icon: MessageSquareText, testid: "nav-comms-generator" },
  { to: "/scheduler", label: "Scheduler", desc: "Plan civic moments and custom dates.", icon: CalendarDays, testid: "nav-comms-scheduler" },
  { to: "/saved", label: "Saved Posts", desc: "Posts you've kept — queued or used.", icon: Bookmark, testid: "nav-comms-saved" },
  { to: "/speeches", label: "Speech Writer", desc: "Full speeches and talking points.", icon: Mic, testid: "nav-comms-speeches" },
  { to: "/graphics", label: "Graphics", desc: "Quick wordmarks, signs, and social cards.", icon: ImageIcon, testid: "nav-comms-graphics", beta: "red" },
];

// "Field Data" — what comes back from voters in the wild.
const fieldDataChildren = [
  { to: "/lawn-signs", label: "Lawn Sign Request Tracker", desc: "Requests, approvals, and delivery routes.", icon: Signpost, testid: "nav-field-lawn-signs" },
  { to: "/volunteers", label: "Volunteer Database", desc: "Sign-ups, skills, status, and contact log.", icon: Users, testid: "nav-field-volunteers" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const billing = useBilling();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [accountType, setAccountType] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/campaign").then(({ data }) => {
      setAccountType(data?.account_type || null);
    }).catch(() => setAccountType(null));
  }, [user]);

  const isCommittee = accountType === "committee";
  const isStaffer = accountType === "staffer";
  const isCandidate = !isCommittee && !isStaffer; // legacy default

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!user) return null;

  // Server-side gated admin tab — we still hide the UI when the user's
  // email doesn't match the owner. Backend enforces real auth on every
  // /api/admin/* call regardless of what's shown here.
  const isOwnerEmail = (user.email || "").toLowerCase() === "scarr299@gmail.com";

  const commsActive = communicationsChildren.some((c) => pathname.startsWith(c.to));
  const fieldActive = fieldDataChildren.some((c) => pathname.startsWith(c.to));

  const linkCls = (active) =>
    `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
      active ? "text-flag-navy bg-slate-100" : "text-slate-600 hover:text-flag-navy hover:bg-slate-50"
    }`;

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="stripe-accent" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/dashboard" data-testid="nav-logo" className="flex items-center gap-2 font-display font-black text-xl text-flag-navy">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-flag-red text-white">
            <Flag className="h-4 w-4" />
          </span>
          Electable
        </Link>

        {/* DESKTOP NAV — Dashboard · Advisor · Communications · Field Data · Voter List · Campaign Calendar · Launch Checklist */}
        <nav className="hidden md:flex items-center gap-1">
          <Link to="/dashboard" data-testid="nav-dashboard" className={linkCls(pathname === "/dashboard")}>
            Dashboard
          </Link>

          <Link to="/advisor" data-testid="nav-advisor" className={linkCls(pathname.startsWith("/advisor"))}>
            Advisor
          </Link>

          {/* Communications dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-communications"
                className={`${linkCls(commsActive)} inline-flex items-center gap-1`}
              >
                Communications
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72" data-testid="nav-communications-menu">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">
                Communications
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {communicationsChildren.map((c) => {
                const Icon = c.icon;
                return (
                  <DropdownMenuItem
                    key={c.to}
                    onSelect={() => navigate(c.to)}
                    data-testid={c.testid}
                    className="cursor-pointer py-2.5"
                  >
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-md bg-flag-navy/5 text-flag-navy">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-900">{c.label}</span>
                          {c.beta && <BetaPill color={c.beta} />}
                        </div>
                        <div className="text-xs text-slate-500">{c.desc}</div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Field Data dropdown — open to all account types */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-field-data"
                className={`${linkCls(fieldActive)} inline-flex items-center gap-1`}
              >
                Field Data
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72" data-testid="nav-field-data-menu">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">
                Field Data
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {fieldDataChildren.map((c) => {
                const Icon = c.icon;
                return (
                  <DropdownMenuItem
                    key={c.to}
                    onSelect={() => navigate(c.to)}
                    data-testid={c.testid}
                    className="cursor-pointer py-2.5"
                  >
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-md bg-flag-navy/5 text-flag-navy">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-900">{c.label}</span>
                          {c.beta && <BetaPill color={c.beta} />}
                        </div>
                        <div className="text-xs text-slate-500">{c.desc}</div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/voters"
            data-testid="nav-voters"
            className={`${linkCls(pathname.startsWith("/voters"))} inline-flex items-center gap-1.5`}
          >
            Voter List
            <ProPill />
          </Link>

          <Link
            to="/finances"
            data-testid="nav-finances"
            className={`${linkCls(pathname.startsWith("/finances"))} inline-flex items-center gap-1.5`}
          >
            Finances
            <ProPill />
          </Link>

          <Link to="/events" data-testid="nav-events" className={linkCls(pathname.startsWith("/events"))}>
            Campaign Calendar
          </Link>

          {isCandidate && (
            <Link to="/launch-checklist" data-testid="nav-launch-checklist" className={linkCls(pathname.startsWith("/launch-checklist"))}>
              Launch Checklist
            </Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-1">
          {billing.loaded && !billing.is_owner && !billing.is_comp && billing.status === "trial" && (
            <Link
              to="/billing"
              data-testid="nav-trial-pill"
              className="mr-1 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 hover:bg-amber-200 px-2.5 py-1 rounded-full transition-colors"
              title="Trial ends soon — choose a plan"
            >
              <Sparkles className="h-3 w-3" />
              {billing.trial_days_left ?? 0}d trial
            </Link>
          )}
          <Link
            to="/billing"
            data-testid="nav-billing"
            className={linkCls(pathname.startsWith("/billing"))}
          >
            <CreditCard className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Billing
          </Link>
          <Link
            to="/settings"
            data-testid="nav-settings"
            className={linkCls(pathname.startsWith("/settings"))}
          >
            <SettingsIcon className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Settings
          </Link>
          {isOwnerEmail && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              aria-label="Owner admin console"
              title="Admin (owner only)"
              className={`ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors ${pathname.startsWith("/admin") ? "bg-slate-100 text-flag-navy" : "text-slate-500 hover:text-flag-navy hover:bg-slate-50"}`}
            >
              <Shield className="h-4 w-4" />
            </Link>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="nav-logout"
            className="ml-1 text-slate-600 hover:text-flag-red"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>

        <button
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* MOBILE NAV — same order as desktop, dropdowns flattened into labeled sections */}
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/dashboard"
              data-testid="nav-dashboard-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50"
            >
              Dashboard
            </Link>
            <Link
              to="/advisor"
              data-testid="nav-advisor-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50"
            >
              Advisor
            </Link>

            <div className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-slate-400">
              Communications
            </div>
            {communicationsChildren.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`${c.testid}-mobile`}
                className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50 inline-flex items-center gap-2"
              >
                {c.label}
                {c.beta && <BetaPill color={c.beta} />}
              </Link>
            ))}

            <div className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-slate-400">
              Field Data
            </div>
            {fieldDataChildren.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`${c.testid}-mobile`}
                className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50 inline-flex items-center gap-2"
              >
                {c.label}
                {c.beta && <BetaPill color={c.beta} />}
              </Link>
            ))}

            <div className="border-t border-slate-100 my-2" />
            <Link
              to="/voters"
              data-testid="nav-voters-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50 inline-flex items-center gap-2"
            >
              Voter List
              <ProPill />
            </Link>
            <Link
              to="/finances"
              data-testid="nav-finances-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50 inline-flex items-center gap-2"
            >
              Finances
              <ProPill />
            </Link>
            <Link
              to="/events"
              data-testid="nav-events-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50"
            >
              Campaign Calendar
            </Link>
            {isCandidate && (
              <Link
                to="/launch-checklist"
                data-testid="nav-launch-checklist-mobile"
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50"
              >
                Launch Checklist
              </Link>
            )}

            <div className="border-t border-slate-100 my-2" />
            <Link
              to="/billing"
              data-testid="nav-billing-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50 inline-flex items-center gap-2"
            >
              <CreditCard className="h-4 w-4" /> Billing
              {billing.loaded && !billing.is_owner && !billing.is_comp && billing.status === "trial" && (
                <span className="ml-1 inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
                  {billing.trial_days_left ?? 0}d trial
                </span>
              )}
            </Link>
            <Link
              to="/settings"
              data-testid="nav-settings-mobile"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-semibold text-slate-700 rounded-md hover:bg-slate-50"
            >
              Settings
            </Link>
            {isOwnerEmail && (
              <Link
                to="/admin"
                data-testid="nav-admin-mobile"
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-sm font-semibold text-slate-500 rounded-md hover:bg-slate-50 inline-flex items-center gap-2"
              >
                <Shield className="h-3.5 w-3.5" /> Admin (owner only)
              </Link>
            )}
            <button
              onClick={handleLogout}
              data-testid="nav-logout-mobile"
              className="px-3 py-3 text-left text-base font-semibold text-flag-red rounded-md hover:bg-slate-50"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
